import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Tag
} from 'lucide-react';
import { 
  Technique,
  TechniqueCreate,
  TechniqueUpdate,
  TechniqueGroupedResponse,
  Category
} from '../types';
import { techniquesApi, categoriesApi } from '../services/api';

interface TestTechniquesProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info') => void;
}

const TestTechniques: React.FC<TestTechniquesProps> = ({ addNotification }) => {
  const [groupedTechniques, setGroupedTechniques] = useState<TechniqueGroupedResponse[]>([]);
  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<number | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grouped' | 'table'>('table');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTechnique, setEditingTechnique] = useState<Technique | null>(null);
  const [formData, setFormData] = useState<TechniqueCreate>({
    category_id: 0,
    name: '',
    description: '',
    prompt: '',
    status: 'pending'
  });

  // Load techniques and categories
  const loadTechniques = async () => {
    try {
      setLoading(true);
      const [techniquesResponse, groupedResponse, categoriesResponse] = await Promise.all([
        techniquesApi.list({
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: searchTerm || undefined,
          limit: 1000
        }),
        techniquesApi.listGrouped({
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: searchTerm || undefined
        }),
        categoriesApi.list({ size: 100 })
      ]);
      
      setTechniques(techniquesResponse.techniques);
      setGroupedTechniques(groupedResponse);
      setCategories(categoriesResponse.categories);
    } catch (error: any) {
      addNotification('Failed to load techniques', 'error');
      console.error('Error loading techniques:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTechniques();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload techniques when filters change (for grouped view)
  useEffect(() => {
    if (viewMode === 'grouped') {
      loadTechniques();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchTerm, viewMode]);

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'KNOWLEDGE_BASE':
        return 'bg-blue-900/50 text-blue-200';
      case 'RAG_BASED':
        return 'bg-green-900/50 text-green-200';
      case 'ADVERSARIAL_BASED':
        return 'bg-red-900/50 text-red-200';
      case 'AGENT_BASED':
        return 'bg-purple-900/50 text-purple-200';
      default:
        return 'bg-slate-700 text-slate-300';
    }
  };

  // Get status display
  const getStatusDisplay = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return { display: 'Approved', color: 'green' };
      case 'declined':
      case 'rejected':
        return { display: 'Declined', color: 'red' };
      case 'pending':
        return { display: 'Pending', color: 'yellow' };
      default:
        return { display: 'Unknown', color: 'gray' };
    }
  };

  // Get category name by ID
  const getCategoryName = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  // Get domain name by category ID
  const getDomainName = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.domain_name : 'UNKNOWN';
  };

  // Get layer by category ID
  const getLayer = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.domain_layer || 'Unknown';
  };

  // Handle create technique
  const handleCreateTechnique = async () => {
    if (!formData.name.trim() || formData.category_id === 0) {
      addNotification('Category and technique name are required', 'error');
      return;
    }

    try {
      await techniquesApi.create(formData);
      addNotification('Technique created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ category_id: 0, name: '', description: '', prompt: '', status: 'pending' });
      loadTechniques();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to create technique';
      addNotification(message, 'error');
      console.error('Error creating technique:', error);
    }
  };

  // Handle edit technique
  const handleEditTechnique = async () => {
    if (!editingTechnique || !formData.name.trim()) {
      addNotification('Technique name is required', 'error');
      return;
    }

    try {
      const updateData: TechniqueUpdate = {
        category_id: formData.category_id,
        name: formData.name,
        description: formData.description || undefined,
        prompt: formData.prompt || undefined,
        status: formData.status
      };

      await techniquesApi.update(editingTechnique.id, updateData);
      addNotification('Technique updated successfully', 'success');
      setShowEditModal(false);
      setEditingTechnique(null);
      setFormData({ category_id: 0, name: '', description: '', prompt: '', status: 'pending' });
      loadTechniques();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update technique';
      addNotification(message, 'error');
      console.error('Error updating technique:', error);
    }
  };

  // Handle delete technique
  const handleDeleteTechnique = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this technique?')) {
      return;
    }

    try {
      await techniquesApi.delete(id);
      addNotification('Technique deleted successfully', 'success');
      loadTechniques();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete technique';
      addNotification(message, 'error');
      console.error('Error deleting technique:', error);
    }
  };

  // Open edit modal
  const openEditModal = (technique: Technique) => {
    setEditingTechnique(technique);
    // Normalize status to lowercase to match dropdown options
    const normalizedStatus = technique.status?.toLowerCase() || 'pending';
    console.log('Opening edit modal for technique:', technique);
    console.log('Original status:', technique.status);
    console.log('Normalized status:', normalizedStatus);
    setFormData({
      category_id: technique.category_id,
      name: technique.name,
      description: technique.description || '',
      prompt: technique.prompt || '',
      status: normalizedStatus
    });
    setShowEditModal(true);
  };

  // Filter techniques based on search, status, and category
  const filteredTechniques = techniques.filter(technique => {
    const matchesSearch = !searchTerm || 
      technique.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (technique.description && technique.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (technique.prompt && technique.prompt.toLowerCase().includes(searchTerm.toLowerCase())) ||
      getCategoryName(technique.category_id).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || technique.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || technique.category_id === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Techniques</h2>
          <p className="text-slate-300">Manage test techniques organized by domains and categories (Domain → Category → Technique)</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'table'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-slate-800 text-slate-200 border-slate-600 hover:bg-slate-700'
              }`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('grouped')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'grouped'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-slate-800 text-slate-200 border-slate-600 hover:bg-slate-700'
              }`}
            >
              Grouped
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Technique
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 p-4 rounded-lg shadow-sm border border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search techniques or categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.domain_name})
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-slate-300">Loading techniques...</p>
        </div>
      ) : filteredTechniques.length === 0 ? (
        <div className="text-center py-12">
          <Tag className="h-12 w-12 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-400 text-lg">No techniques found</p>
          <p className="text-slate-400 text-sm">
            {techniques.length === 0
              ? 'Create your first technique to get started'
              : 'Try adjusting your filters'
            }
          </p>
        </div>
      ) : viewMode === 'table' ? (
        /* Table View */
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-medium text-slate-100">
              Techniques ({filteredTechniques.length})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Domain (Layer)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Technique Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Prompt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {filteredTechniques.map((technique) => {
                  const statusInfo = getStatusDisplay(technique.status);
                  const categoryName = getCategoryName(technique.category_id);
                  const domainName = getDomainName(technique.category_id);
                  const layer = getLayer(technique.category_id);
                  
                  return (
                    <tr key={technique.id} className="hover:bg-slate-700/30">
                      {/* Domain (Layer) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(domainName || '')}`}>
                            <Tag className="h-3 w-3 mr-1" />
                            {domainName?.replace('_', ' ') || 'Unknown'}
                          </span>
                          {layer && layer !== 'Unknown' && (
                            <div className="text-xs text-slate-400 italic">
                              {layer}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* Category */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-100">{categoryName}</div>
                      </td>
                      
                      {/* Technique Name */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-100">{technique.name}</div>
                      </td>
                      
                      {/* Description */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-100 max-w-md">
                          {technique.description ? (
                            <div className="break-words whitespace-normal">
                              {technique.description}
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No description</span>
                          )}
                        </div>
                      </td>
                      
                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          statusInfo.color === 'green' ? 'bg-green-900/50 text-green-200' :
                          statusInfo.color === 'yellow' ? 'bg-amber-900/50 text-amber-200' :
                          statusInfo.color === 'red' ? 'bg-red-900/50 text-red-200' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {statusInfo.display}
                        </span>
                      </td>
                      
                      {/* Prompt */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-100 max-w-md">
                          {technique.prompt ? (
                            <div className="break-words whitespace-normal">
                              {technique.prompt}
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">No prompt</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(technique)}
                            className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTechnique(technique.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-200 bg-red-900/50 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Grouped View */
        (() => {
          const filteredGroups = groupedTechniques.filter((group) => {
            // Filter by category if selected
            const matchesCategory = categoryFilter === 'all' || group.category_id === categoryFilter;
            // Only show groups that have techniques
            const hasTechniques = group.techniques.length > 0;
            return matchesCategory && hasTechniques;
          });

          return filteredGroups.length === 0 ? (
            <div className="text-center py-12 bg-slate-800 rounded-lg shadow-sm border border-slate-700">
              <Tag className="h-12 w-12 mx-auto mb-3 text-slate-500" />
              <p className="text-slate-400 text-lg">No technique groups found</p>
              <p className="text-slate-400 text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGroups.map((group) => (
            <div key={group.category_id} className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
              {/* Group Header */}
              <div className="px-6 py-4 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(group.domain_name || '')}`}>
                      <Tag className="h-3 w-3 mr-1" />
                      {group.domain_name}
                    </span>
                    <h3 className="text-lg font-medium text-slate-100">
                      {group.category_name}
                    </h3>
                    <span className="text-sm text-slate-400">
                      {group.techniques.length} technique{group.techniques.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              {/* Techniques List */}
              <div className="divide-y divide-slate-700">
                {group.techniques.map((technique) => {
                  const statusInfo = getStatusDisplay(technique.status);
                  return (
                    <div key={technique.id} className="px-6 py-4 hover:bg-slate-700/30">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-sm font-medium text-slate-100">
                              {technique.name}
                            </h4>
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              statusInfo.color === 'green' ? 'bg-green-900/50 text-green-200' :
                              statusInfo.color === 'yellow' ? 'bg-amber-900/50 text-amber-200' :
                              statusInfo.color === 'red' ? 'bg-red-900/50 text-red-200' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {statusInfo.display}
                            </span>
                          </div>
                          {technique.description && (
                            <p className="mt-1 text-sm text-slate-300 max-w-2xl">
                              {technique.description}
                            </p>
                          )}
                          {technique.prompt && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-slate-400 mb-1">Prompt:</p>
                              <p className="text-sm text-slate-200 max-w-2xl bg-slate-700 p-2 rounded border border-slate-600">
                                {technique.prompt}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(technique)}
                            className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTechnique(technique.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-200 bg-red-900/50 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
            </div>
          );
        })()
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Technique</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value={0}>Select category...</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.domain_name})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Technique Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter technique name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter technique description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Prompt
                  </label>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter prompt text for this technique"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTechnique}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingTechnique && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Technique</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value={0}>Select category...</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.domain_name})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Technique Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter technique name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter technique description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Prompt
                  </label>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter prompt text for this technique"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTechnique}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestTechniques;
